import { motion } from "motion/react";

const sections = [
  {
    title: "1. Information We Collect",
    items: [
      "Basic contact information you share through forms, email, or donations.",
      "Technical data such as page visits, browser details, device information, and IP address.",
      "Donation and sponsorship details when you support a project or campaign.",
      "Optional content you choose to submit, such as feedback, messages, or event participation details.",
    ],
  },
  {
    title: "2. How We Use Your Data",
    items: [
      "To respond to enquiries and provide the services you request.",
      "To process donations, acknowledgements, and campaign updates.",
      "To improve the website, measure performance, and understand visitor behaviour.",
      "To send project updates, newsletters, and other communications where you have opted in.",
      "To protect the website against misuse, fraud, or unauthorized access.",
    ],
  },
  {
    title: "3. Cookies and Tracking",
    items: [
      "We may use essential cookies for site functionality and session continuity.",
      "We may use analytical cookies to understand how people use the website.",
      "You can manage cookies in your browser settings, although some features may stop working properly if cookies are disabled.",
    ],
  },
  {
    title: "4. Sharing and Retention",
    items: [
      "We may share data with trusted service providers who help us operate the website, process payments, or deliver communications.",
      "We may disclose information when required by law or to protect our rights, users, and systems.",
      "We keep personal data only for as long as necessary for the purpose it was collected or as required by law.",
    ],
  },
  {
    title: "5. Your Rights",
    items: [
      "You can request access to the personal data we hold about you.",
      "You can request corrections, updates, or deletion where applicable.",
      "You can withdraw consent from non-essential communications at any time.",
      "You can contact us if you want to unsubscribe from future correspondence.",
    ],
  },
];

export function PrivacyPolicy() {
  return (
    <main className="bg-white text-foreground">
      <section className="relative overflow-hidden border-b border-border bg-gradient-to-br from-primary/10 via-white to-secondary/10 py-24">
        <div className="absolute inset-0 opacity-40 [background-image:radial-gradient(circle_at_top_left,_rgba(56,111,91,0.12),_transparent_32%),radial-gradient(circle_at_bottom_right,_rgba(178,97,66,0.10),_transparent_30%)]" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-sm uppercase tracking-[0.3em] text-primary mb-4">
              Privacy Policy
            </p>
            <h1 className="text-5xl md:text-6xl mb-6 font-bold tracking-tight">
              Website and Donor Privacy Policy
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed max-w-3xl">
              This page explains what information we collect, how we use it, and the
              choices you have when interacting with the Immerse Foundation website
              and its donation or campaign features.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          {sections.map((section, index) => (
            <motion.section
              key={section.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.08 }}
              className="rounded-3xl border border-border bg-muted/20 p-8 shadow-sm"
            >
              <h2 className="text-3xl mb-5">{section.title}</h2>
              <ul className="space-y-4 text-lg text-muted-foreground leading-relaxed list-disc pl-6">
                {section.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </motion.section>
          ))}

          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-3xl border border-border bg-primary/5 p-8"
          >
            <h2 className="text-3xl mb-4">Contact and Updates</h2>
            <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
              <p>
                If you want to access, update, or remove your information, or if you
                have any privacy-related questions, please contact us at
                hello@immerse.foundation.
              </p>
              <p>
                We may update this policy from time to time. Any changes will be posted
                on this page so you can review them whenever you visit the site.
              </p>
              <p className="text-sm uppercase tracking-[0.2em] text-primary">
                Last reviewed: 29 September 2023
              </p>
            </div>
          </motion.section>
        </div>
      </section>
    </main>
  );
}
